import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import { useSnackbarStore } from 'stores/zustand/snackbarStore';

import type { ServerError } from '../../models';
import type { GetNotificationPreferenceResponse } from '../../models/reqres';
import type {
    NotificationPreferences,
    NotificationSettings,
    NotificationType,
    UserGetNotificationSettingsResponse,
} from '../../models/subscriptions';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function getEmailPreference(
    userId: string,
    onSuc: (preferences: NotificationPreferences) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetNotificationPreferenceResponse>(`/users/${userId}/notifications/preferences`)
        .then((response) => {
            onSuc(response.data.preferences.email_preferences);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function getNotificationPreferences(
    userId: string,
    onSuc: (prefs: { email: NotificationPreferences; push: NotificationPreferences }) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<GetNotificationPreferenceResponse>(`/users/${userId}/notifications/preferences`)
        .then((response) => {
            onSuc({
                email: response.data.preferences.email_preferences ?? [],
                push: response.data.preferences.push_preferences ?? [],
            });
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function changeBothPreferences(
    userId: string,
    emailPreferences: NotificationPreferences,
    pushPreferences: NotificationPreferences,
    onSucMessage: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .patch(
            `/users/${userId}/subscribe`,
            { email_preferences: emailPreferences, push_preferences: pushPreferences },
            { cancelToken: cancelRef.token },
        )
        .then(() => {
            if (onSucMessage) {
                useSnackbarStore.getState().actions.pushMessage({
                    text: onSucMessage,
                    variant: 'positive',
                });
            }
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(onFinally);

    return cancelRef;
}

export function changeEmailPreference(
    userId: string,
    emailPreferences: NotificationPreferences,
    onSucMessage: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .patch(`/users/${userId}/subscribe`, { email_preferences: emailPreferences }, { cancelToken: cancelRef.token })
        .then(() => {
            if (onSucMessage) {
                useSnackbarStore.getState().actions.pushMessage({
                    text: onSucMessage,
                    variant: 'positive',
                });
            }
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getNotificationSettings(
    userId: string,
    notificationType: NotificationType,
    onSuc: (batchIntervalSeconds: number, settings: NotificationSettings) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<UserGetNotificationSettingsResponse>(`/users/${userId}/notifications/settings/${notificationType}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => onSuc(response.data.batch_interval, response.data.settings))
        .catch((error) => handleErrorCallback(error, onErr))
        .finally(onFinally);

    return cancelRef;
}

export function updateNotificationSettings(
    userId: string,
    notificationType: NotificationType,
    batchIntervalSeconds: number,
    settings: NotificationSettings,
    onSucMessage: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .put(
            `/users/${userId}/notifications/settings/${notificationType}`,
            { settings, batch_interval: batchIntervalSeconds },
            { cancelToken: cancelRef.token },
        )
        .then(() => {
            useSnackbarStore.getState().actions.pushMessage({
                text: onSucMessage,
                variant: 'positive',
            });
            onSuc();
        })
        .catch((error) => handleErrorCallback(error, onErr))
        .finally(onFinally);

    return cancelRef;
}

export function deleteNotificationSettings(
    userId: string,
    notificationType: NotificationType,
    onSucMessage: string,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .delete(`/users/${userId}/notifications/settings/${notificationType}`, {
            cancelToken: cancelRef.token,
        })
        .then(() => {
            useSnackbarStore.getState().actions.pushMessage({
                text: onSucMessage,
                variant: 'positive',
            });
            onSuc();
        })
        .catch((error) => handleErrorCallback(error, onErr))
        .finally(onFinally);

    return cancelRef;
}
